import React, { useState, useEffect } from 'react';
import { Plus, Upload, Edit, Trash2, Phone, Mail, MapPin, Building2, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchLeads();
  }, [filterStatus]);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filterStatus === 'all' 
        ? `${API_URL}/api/leads`
        : `${API_URL}/api/leads?status=${filterStatus}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const parseGPTLeads = (text) => {
    const leads = [];
    const leadBlocks = text.split('---').filter(block => block.trim());
    
    leadBlocks.forEach(block => {
      const lines = block.trim().split('\n');
      const lead = {};
      
      lines.forEach(line => {
        const [key, ...valueParts] = line.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          const keyLower = key.trim().toUpperCase();
          if (keyLower === 'COMPANY') lead.company_name = value;
          else if (keyLower === 'CONTACT') lead.contact_name = value;
          else if (keyLower === 'PHONE') lead.phone = value;
          else if (keyLower === 'EMAIL') lead.email = value;
          else if (keyLower === 'ADDRESS') lead.address = value;
          else if (keyLower === 'DISTANCE') lead.distance = value;
          else if (keyLower === 'TYPE') lead.industry_type = value;
          else if (keyLower === 'NOTES') lead.notes = value;
        }
      });
      
      if (lead.company_name) {
        leads.push(lead);
      }
    });
    
    return leads;
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      const parsedLeads = parseGPTLeads(importText);
      
      if (parsedLeads.length === 0) {
        alert('No valid leads found in the text. Please check the format.');
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/leads/bulk-import`,
        parsedLeads,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Successfully imported ${parsedLeads.length} leads!`);
      setShowImportModal(false);
      setImportText('');
      fetchLeads();
    } catch (error) {
      console.error('Error importing leads:', error);
      alert('Failed to import leads. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/leads/${leadId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-purple-100 text-purple-800',
      in_pipeline: 'bg-green-100 text-green-800',
      won: 'bg-emerald-100 text-emerald-800',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const statusOptions = [
    { value: 'all', label: 'All Leads' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'in_pipeline', label: 'In Pipeline' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen ops-canvas p-6 flex items-center justify-center">
        <div className="text-slate-900">Loading leads...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ops-canvas p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Leads</h1>
            <p className="text-slate-500">Manage your sales leads and prospects</p>
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Upload className="h-5 w-5" />
            Import Leads
          </button>
        </div>

        {/* Filters */}
        <div className="bg-slate-900/5 rounded-lg p-4 mb-6">
          <div className="flex gap-2">
            {statusOptions.map(option => (
              <button
                key={option.value}
                onClick={() => setFilterStatus(option.value)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filterStatus === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-900/5 text-slate-900 hover:bg-slate-900/10'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-slate-900/5 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Distance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                      No leads found. Click "Import Leads" to add some!
                    </td>
                  </tr>
                ) : (
                  leads.map(lead => (
                    <tr key={lead.id} className="hover:bg-slate-900/5">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-blue-700" />
                          <div>
                            <div className="font-medium text-slate-900">{lead.company_name}</div>
                            <div className="text-xs text-slate-500">{lead.industry_type}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">{lead.contact_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {lead.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                              <Phone className="h-3 w-3" />
                              {lead.phone}
                            </div>
                          )}
                          {lead.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                              <Mail className="h-3 w-3" />
                              {lead.email}
                            </div>
                          )}
                          {lead.address && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <MapPin className="h-3 w-3" />
                              {lead.address}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-900">{lead.distance || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          <option value="in_pipeline">In Pipeline</option>
                          <option value="won">Won</option>
                          <option value="lost">Lost</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => deleteLead(lead.id)}
                          className="text-red-600 hover:text-red-700 transition-colors"
                          title="Delete lead"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Import Leads from GPT</h2>
            
            <div className="mb-4 p-4 bg-blue-50 border border-blue-300 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Format:</strong> Paste the output from ChatGPT in this format:
              </p>
              <pre className="text-xs text-slate-700 bg-white p-2 rounded overflow-x-auto">
{`COMPANY: ABC Manufacturing
CONTACT: John Smith
PHONE: (555) 123-4567
EMAIL: john@abc.com
ADDRESS: 123 Main St, City, State
DISTANCE: 8 miles
TYPE: Manufacturing
NOTES: Large facility, needs welding
---
COMPANY: XYZ Foods
...`}
              </pre>
            </div>

            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="Paste GPT output here..."
              className="w-full h-64 bg-white text-slate-900 p-4 rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none mb-4"
            />

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-100 text-slate-900 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importing || !importText.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? 'Importing...' : 'Import Leads'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
